import type { ComponentType, ReactNode } from 'react';
import type { PlainKey } from '@/i18n';
import type { LensRail } from '@/domain/lens-view';
import { Graph, Kanban, ListChecks, Table } from './icons';

/**
 * The glyph of each layout (docs/guide/views.md "One view, four layouts"):
 * a list wears the table, a kanban its columns, the map its graph, a
 * checklist its ticks. The one source for a layout's glyph and word: the
 * layout switch on a view's page (`RAILS` in lens-surface.tsx), a board's
 * layout (its `layout`, the same vocabulary without the map), the overviews'
 * cards and the item page's board rows all draw from here.
 *
 * The list IS the table, so it wears the Table glyph: Rows is Group by's
 * alone, and the compact bar's layout trigger does not repeat the glyph
 * sitting right under it in the head (Leon, 2026-09-27).
 */
const LAYOUT_ICON: Record<
  LensRail,
  ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
> = {
  list: Table,
  board: Kanban,
  map: Graph,
  checklist: ListChecks,
};

/** A layout's glyph, sized by the caller's class. */
export function LayoutGlyph({ layout, className }: { layout: LensRail; className?: string }) {
  const Icon = LAYOUT_ICON[layout];
  return <Icon className={className} aria-hidden />;
}

export const LAYOUT_GLYPH: Record<LensRail, ReactNode> = {
  list: <LayoutGlyph layout="list" />,
  board: <LayoutGlyph layout="board" />,
  map: <LayoutGlyph layout="map" />,
  checklist: <LayoutGlyph layout="checklist" />,
};

/** Each layout's word, as the layout switch names it. */
export const LAYOUT_WORD: Record<LensRail, PlainKey> = {
  list: 'lenses.rail.list',
  board: 'lenses.rail.board',
  map: 'lenses.rail.map',
  checklist: 'lenses.rail.checklist',
};
